import React from 'react'

import { TableCol } from '../../../../../components/pdf/Table'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { Bullets, Paragraph, SectionHeading, SubHeading } from './_shared/Prose'
import MatrizRiscoTable from './_shared/MatrizRiscoTable'
import { RedDataCell, RedHeadCell, RedRow, RedTable } from './_shared/BradescoTable'
import { PRIORIDADE_CONTROLE_COLORS, TABLE_ACCENT_DARK_RED, TABLE_BORDER_RED } from '../_helpers/brand'

// Tabela 9.2 "Critérios de Gradação da Probabilidade" (Peso | Risco | Probabilidade).
// Texto descritivo literal do modelo — distinto do vetor curto PROBABILIDADE_REFERENCIA
// (usado no cálculo dinâmico e na Seção 10/referência), que traz só peso+rótulo.
const CRITERIOS_PROBABILIDADE = [
  {
    peso: 1,
    risco: 'Muito Baixo',
    descricao:
      'Não há exposição com o perigo ou se ocorrer será de forma rara. Medidas de Controle: Existência de controles eficazes excelentes ou eliminação do risco (Ex: EPC, EPI, medidas administrativas e processos e práticas adequadas).'
  },
  {
    peso: 2,
    risco: 'Baixo',
    descricao:
      'A exposição perigo é eventual (baixa probabilidade), ocorre por curto período de tempo e apresenta baixa relevância. Medida de Controle: Existência de controles eficazes conforme normais legais (Ex: existência EPC, manutenções preventivas/corretivas).'
  },
  {
    peso: 3,
    risco: 'Moderado',
    descricao:
      'A exposição ao perigo é de forma intermitente (pode acontecer, mas não é comum), por curto período de tempo. Medida de Controle: Controle adequado com pequenas deficiências na operação ou manutenção (Ex: Utilização de EPI e instalação de dispositivos de advertência).'
  },
  {
    peso: 4,
    risco: 'Alto',
    descricao:
      'A exposição ao perigo é de forma permanente, por longo período de tempo. Medida de Controle: Controle incompleto ou com deficiências relevantes (Ex: Depende inteiramente do trabalhador - treinamentos e procedimentos escritos).'
  },
  {
    peso: 5,
    risco: 'Severo ou Crítico',
    descricao:
      'A exposição ao perigo é permanente (com alta probabilidade de acontecer), por longo período de tempo. Medida de Controle: As medidas de controle são inexistentes ou totalmente inadequadas (Ex: Não há medidas de controle, apenas o conhecimento do risco).'
  }
]

// Tabela 9.2 "Critérios de Gradação da Severidade" (Peso | Efeito | Severidade), com
// os critérios qualitativos por tipo de agente e o critério quantitativo (Anexos da NR15/NR32).
const CRITERIOS_SEVERIDADE = [
  {
    peso: 1,
    efeito: 'Irrelevante',
    biologico: 'Sem exposição ou possibilidade de causar efeitos à saúde.',
    quimico: '(Anexo 13 da NR15) Sem exposição ou possibilidade de causar efeitos à saúde.',
    acidente: 'Ocorrência com lesão leve sem necessidade atenção médica, incômodos ou mal estar (sem dias perdidos).',
    ergonomico: 'Fatores ergonômicos que não irão gerar lesões ou doenças a curto, médio ou longo prazo.',
    quantitativo: '(Físico/Químico Anexo 11 da NR15) Abaixo do nível de ação (quando aplicável), bem como abaixo do limite de tolerância.'
  },
  {
    peso: 2,
    efeito: 'Baixo',
    biologico: '(Anexo I da NR32) Classe de risco 1.',
    quimico: '(Anexo 13 da NR15) Atividades elencadas no grau mínimo.',
    acidente:
      'Ocorrência com lesão ou doenças sérias reversíveis (não impede do acidentado voltar trabalhar no dia seguinte sem prejuízo a execução das funções).',
    ergonomico: 'Fatores ergonômicos que podem gerar lesões ou doenças a longo prazo.',
    quantitativo: '(Físico/Químico Anexo 11 da NR15) Até o nível de ação (quando aplicável), bem como abaixo do limite de tolerância.'
  },
  {
    peso: 3,
    efeito: 'Moderado',
    biologico: '(Anexo I da NR32) Classe de risco 2.',
    quimico: '(Anexo 13 da NR15) Atividades elencadas no grau médio.',
    acidente:
      'Ocorrência com lesão ou doenças críticas irreversíveis que podem limitar a capacidade funcional (com dias perdidos).',
    ergonomico: 'Fatores ergonômicos que podem gerar lesões ou doenças a médio prazo.',
    quantitativo:
      '(Físico/Químico Anexo 11 da NR15) Níveis acima do nível de ação (quando aplicável), porém ainda abaixo dos limites de exposição estabelecidos pelas normas vigentes.'
  },
  {
    peso: 4,
    efeito: 'Alto',
    biologico: '(Anexo I da NR32) Classe de risco 3.',
    quimico: '(Anexo 13 da NR15) Atividades elencadas no grau máximo.',
    acidente:
      'Risco iminente, ocorrências com lesão ou doença incapacitante (impedindo a volta do trabalhador no dia seguinte, resultando em perda parcial ou total de sua capacidade funcional).',
    ergonomico: 'Fatores ergonômicos que podem gerar lesões ou doenças a curto prazo.',
    quantitativo: '(Físico/Químico Anexo 11 da NR15) Acima dos níveis dos limites de tolerância (quando aplicável).'
  },
  {
    peso: 5,
    efeito: 'Extrema',
    biologico: '(Anexo I da NR32) Classe de risco 4.',
    quimico: '(Anexo 13 da NR15) Atividades elencadas no grau máximo.',
    acidente:
      'Ocorrências com mortes ou incapacidades múltiplas (impedindo a voltar ao trabalho no dia seguinte, resultando em perda permanente de sua capacidade de trabalho ou óbito).',
    ergonomico: 'Fatores ergonômicos que podem gerar lesões ou doenças imediatos.',
    quantitativo: '(Físico/Químico) Níveis acima do limite de tolerância.'
  }
]

// Tabela 9.4 "Avaliação e Prioridade de Controle".
const PRIORIDADE_CONTROLE = [
  { classificacao: 'Trivial', gradacao: 'Igual a 1', nivel: 'Sem necessidade de ações.' },
  { classificacao: 'Baixo', gradacao: '2 e 3', nivel: 'Manter as medidas existentes e suas manutenções vigentes' },
  {
    classificacao: 'Moderado',
    gradacao: '4 a 6',
    nivel: 'Considerar possíveis ações de melhorias, realizando estudos para ações definitivas'
  },
  {
    classificacao: 'Alto',
    gradacao: '8 a 12',
    nivel: 'Devem ser realizadas ações para reduzir ou eliminar o perigo mesmo em caráter provisório.'
  },
  {
    classificacao: 'Crítico',
    gradacao: '15 a 25',
    nivel: 'Ações imediatas para redução ou eliminação do perigo, com possibilidade de interrupção imediata da atividade.'
  }
]

const CampoCelula = ({ label, children }) => (
  <>
    <Text size="xsmall" bold inline>
      {label}:{' '}
    </Text>
    <Text size="xsmall" inline>
      {children}
    </Text>
    <Separator weight={2} />
  </>
)

/**
 * Seção 9 - Classificação dos Riscos: Físico, Químico, Biológico, Ergonômico
 * Ambientais e Acidente. Texto literal do modelo aprovado por Julia Buzzo ("Modelo PGR
 * 2026 - Banco Bradesco_Varejo"). Substitui o placeholder institucional anterior
 * (arquivo era `09.MetodologiaErgocheckSection.js` — o conteúdo do método ERGOCHECK
 * para classificação ergonômico/psicossocial é a Seção 10, ver
 * `10.ClassificacaoRiscosErgonomicoPsicossocialSection.js`).
 *
 * As tabelas 9.2 (Gradação) e 9.3 (Matriz de Risco) reaproveitam os mesmos vetores de
 * referência de `_helpers/riskEngine.js` já usados no cálculo dinâmico do Inventário de
 * Riscos (Seção 18). O texto descritivo completo de cada gradação (colunas Risco/Efeito)
 * é literal do modelo e não está no helper, por isso vive apenas neste arquivo.
 *
 * CORREÇÃO (Julia Buzzo, "o ponto 10 11 e 12 estão errado"): este texto/tabelas de 9.2/9.3
 * também tinham sido duplicados, sob títulos incorretos, nos arquivos que se chamavam
 * `10.GraduacaoProbabilidadeSection.js`/`11.GraduacaoSeveridadeSection.js`/
 * `12.MatrizRiscoSection.js` — removida a duplicata; esses três slots de arquivo agora
 * contêm as Seções 10, 11 e 12 reais do modelo (ver `PdfDocument.js`).
 *
 * Tabela 9.4 "Avaliação e Prioridade de Controle": Julia Buzzo reportou que a coluna
 * "Classificação" não batia com o modelo enviado — na versão anterior a coluna era
 * texto simples sem cor de fundo. Corrigido para o heat-map de 5 níveis confirmado por
 * amostragem de pixel do recorte do modelo (Trivial=azul #00B0F0, Baixo=verde #8ED873,
 * Moderado=amarelo #FFFF00, Alto=laranja #FFC000, Crítico=vermelho escuro
 * `TABLE_ACCENT_DARK_RED`/#C00000 com texto branco) — ver `PRIORIDADE_CONTROLE_COLORS`
 * em `_helpers/brand.js`. O cabeçalho da tabela também foi ajustado para o mesmo
 * vermelho escuro (a amostragem mostrou #C00000, não o vermelho vivo padrão
 * `TABLE_BORDER_RED` usado nas demais tabelas em grade). Texto/valores de Gradação e
 * Nível de Risco Ocupacional (`PRIORIDADE_CONTROLE`) já batiam com o modelo e não foram
 * alterados; nenhuma lógica de cálculo foi tocada.
 */
const ClassificacaoRiscosFisicoQuimicoBiologicoSection = () => (
  <View id="9">
    <Separator weight={15} />
    <SectionHeading numero={9}>
      CLASSIFICAÇÃO DOS RISCOS: FÍSICO, QUÍMICO, BIOLÓGICO, ERGONÔMICO AMBIENTAIS E ACIDENTE
    </SectionHeading>

    <SubHeading id="9.1">9.1. Metodologia de Avaliação</SubHeading>
    <Paragraph>
      A ferramenta adotada para classificação dos riscos ocupacionais é a metodologia AIHA (American Industrial
      Hygiene Association), aplicada aos riscos físicos, químicos, biológicos, ergonômicos/biomecânico e acidentes.
    </Paragraph>
    <Paragraph>
      O termo "critério AIHA" refere-se a um conjunto de diretrizes utilizadas na análise e avaliação de riscos
      ocupacionais. Desenvolvido pela American Industrial Hygiene Association (AIHA), esse critério é amplamente
      reconhecido na área de higiene ocupacional e tem como objetivo orientar a antecipação, reconhecimento,
      avaliação e controle dos perigos presentes no ambiente de trabalho.
    </Paragraph>
    <Paragraph>
      A metodologia AIHA permite determinar a probabilidade de ocorrência e a severidade dos eventos que possam
      causar danos à saúde dos trabalhadores ou ao meio ambiente. Com base nessa análise, é possível priorizar ações
      e definir medidas de controle eficazes, contribuindo para a promoção de ambientes de trabalho mais seguros e
      saudáveis.
    </Paragraph>

    <Paragraph>Termos e Definições:</Paragraph>
    <Bullets
      items={[
        'AIHA (American Industrial Hygiene Association): É uma organização que promove a higiene ocupacional, focando na prevenção de doenças e lesões relacionadas ao trabalho.',
        'Critério AIHA: Este critério é uma ferramenta que ajuda a identificar, avaliar e controlar riscos em um ambiente de trabalho, incluindo riscos químicos, físicos, biológicos e ergonômicos.',
        'Análise de Risco: A avaliação do risco é um processo que envolve a identificação de perigos, a análise de riscos, a avaliação de probabilidade e gravidade, e a implementação de medidas de controle.'
      ]}
    />

    <SubHeading id="9.2">9.2. Gradação de Probabilidade e Severidade</SubHeading>
    <Paragraph>
      As tabelas de gradação de probabilidade e severidade sugeridas são as tabelas da AIHA - American Industrial
      Hygiene Association (recomendadas pela Fundacentro). Todas elas possuem gradações de 1 a 5, que vão determinar
      a classificação da probabilidade, isto é, refere-se à chance de um evento prejudicial ocorrer e severidade,
      isto é, o grau de dano que um evento prejudicial pode causar.
    </Paragraph>
    <Paragraph>As avaliações podem ser divididas como:</Paragraph>
    <Bullets
      items={[
        'Avaliação Qualitativa: utiliza a observação e a descrição das condições de trabalho para identificar riscos;',
        'Avaliação Quantitativa: mede e quantifica a exposição dos riscos usando equipamentos e métodos de amostragem, com a finalidade de comparar com limites de tolerância.'
      ]}
    />
    <Paragraph>
      As gradações de probabilidade são 5 (cinco): Muito Baixo (1); Baixo (2); Moderado (3); Alto (4) e Severo ou
      Crítico (5). Nas avaliações qualitativas, de acordo com o controle e exposição ao risco, determina-se de 1 a 5
      o nível de probabilidade. Em avaliações quantitativas, a probabilidade é classificada de acordo com a
      porcentagem do valor de exposição ao LEO - Limite de Exposição Ocupacional.
    </Paragraph>

    <Text semibold size="xsmall">
      Tabela — Critérios de Gradação da Probabilidade
    </Text>
    <Separator weight={4} />
    <View style={{ overflowX: 'auto' }}>
      <RedTable width="100%">
        <RedRow>
          <RedHeadCell width="8%">Peso</RedHeadCell>
          <RedHeadCell width="15%">Risco</RedHeadCell>
          <RedHeadCell align="left">Probabilidade</RedHeadCell>
        </RedRow>
        {CRITERIOS_PROBABILIDADE.map((item, index) => (
          <RedRow key={item.peso} striped={index % 2 === 1}>
            <RedDataCell align="center" bold>
              {item.peso}
            </RedDataCell>
            <RedDataCell align="center" bold>
              {item.risco}
            </RedDataCell>
            <RedDataCell align="left">{item.descricao}</RedDataCell>
          </RedRow>
        ))}
      </RedTable>
    </View>

    <Separator weight={10} />

    <Paragraph>
      As gradações de severidade são 5 (cinco): Irrelevante (1); Baixo (2); Moderado (3); Alto (4) e Extrema (5). A
      severidade é classificada de 1 a 5, de acordo com o nível de consequência à exposição.
    </Paragraph>

    <Text semibold size="xsmall">
      Tabela — Critérios de Gradação da Severidade
    </Text>
    <Separator weight={4} />
    <View style={{ overflowX: 'auto' }}>
      <RedTable width="100%">
        <RedRow>
          <RedHeadCell width="6%">Peso</RedHeadCell>
          <RedHeadCell width="12%">Efeito</RedHeadCell>
          <RedHeadCell align="left">Severidade (Qualitativo / Quantitativo)</RedHeadCell>
        </RedRow>
        {CRITERIOS_SEVERIDADE.map((item, index) => (
          <RedRow key={item.peso} striped={index % 2 === 1}>
            <RedDataCell align="center" bold>
              {item.peso}
            </RedDataCell>
            <RedDataCell align="center" bold>
              {item.efeito}
            </RedDataCell>
            <TableCol color={TABLE_BORDER_RED}>
              <CampoCelula label="Biológico">{item.biologico}</CampoCelula>
              <CampoCelula label="Químico">{item.quimico}</CampoCelula>
              <CampoCelula label="Acidente">{item.acidente}</CampoCelula>
              <CampoCelula label="Ergonômico Biomecânicos">{item.ergonomico}</CampoCelula>
              <CampoCelula label="Quantitativo">{item.quantitativo}</CampoCelula>
            </TableCol>
          </RedRow>
        ))}
      </RedTable>
    </View>

    <Separator weight={15} />

    <SubHeading id="9.3">9.3. Matriz de Risco</SubHeading>
    <Paragraph>
      Na matriz de risco apresentada abaixo, classifica o nível de risco ocupacional através da
      multiplicação/correlação entre probabilidade x severidade, com base nas avaliações realizadas em campo. Essa
      abordagem permite classificar os níveis de risco inerentes às atividades desenvolvidas nas dependências do
      Banco Bradesco S.A., orientando a definição da priorização das medidas de controle e ações preventivas de
      forma sistemática e fundamentada.
    </Paragraph>
    <Paragraph>
      Os níveis de risco presentes na matriz são 25: Trivial (1); Baixo (2-3); Moderado (4-6); Alto (8-12); Crítico
      (15-20) e Intolerável (25). Cada nível de risco possui o seu método de controle sugerido, baseado na
      estimativa (grau de certeza) da avaliação, onde os riscos de níveis mais altos têm prioridade de ação.
    </Paragraph>
    <Paragraph>
      O nível de risco ocupacional é uma classificação do risco que os trabalhadores estão expostos em função da
      natureza da atividade desempenhada, das condições de trabalho e dos agentes de risco presentes no ambiente.
      Ele é determinado com base na combinação da probabilidade e da severidade do risco, sendo um indicador da
      gravidade do risco para a saúde e segurança do colaborador.
    </Paragraph>

    <Text semibold size="xsmall">
      Matriz de Risco - Classificação de Nível de Risco Ocupacional
    </Text>
    <Separator weight={4} />
    <MatrizRiscoTable />

    <Separator weight={15} />

    <SubHeading id="9.4">9.4. Avaliação e Prioridade de Controle</SubHeading>
    <Paragraph>
      O Banco Bradesco S.A. estabelecerá aqui suas prioridades e metas de avaliação e controle, segundo o estudo dos
      riscos. As prioridades são estabelecidas conforme a intensidade ou concentração dos agentes de risco, além dos
      custos de implantação das medidas de controle e complexidade dos projetos.
    </Paragraph>

    <Text semibold size="xsmall">
      Tabela — Avaliação e Prioridade de Controle
    </Text>
    <Separator weight={4} />
    <RedTable width="100%">
      <RedRow>
        <RedHeadCell bgColor={TABLE_ACCENT_DARK_RED}>Classificação</RedHeadCell>
        <RedHeadCell bgColor={TABLE_ACCENT_DARK_RED}>Gradação</RedHeadCell>
        <RedHeadCell align="left" bgColor={TABLE_ACCENT_DARK_RED}>
          Nível de Risco Ocupacional
        </RedHeadCell>
      </RedRow>
      {PRIORIDADE_CONTROLE.map(item => {
        const cor = PRIORIDADE_CONTROLE_COLORS[item.classificacao]
        return (
          <RedRow key={item.classificacao}>
            <TableCol color={TABLE_BORDER_RED} bgColor={cor.bg}>
              <Text align="center" bold color={cor.text}>
                {item.classificacao}
              </Text>
            </TableCol>
            <RedDataCell align="center">{item.gradacao}</RedDataCell>
            <RedDataCell align="left">{item.nivel}</RedDataCell>
          </RedRow>
        )
      })}
    </RedTable>

    <Separator weight={15} />

    <SubHeading id="9.5">9.5 Implementação da Medidas de Controle</SubHeading>
    <Paragraph>
      O estudo, desenvolvimento e implantação das medidas de controle coletivas obedecem à seguinte hierarquia:
    </Paragraph>
    <Bullets
      items={[
        '1. Medidas que eliminam ou reduzem a utilização ou a formação de agentes prejudiciais à saúde;',
        '2. Medidas que previnam a liberação ou disseminação desses agentes no ambiente de trabalho;',
        '3. Medidas que reduzam os níveis ou a concentração desses agentes no ambiente de trabalho.'
      ]}
    />
    <Paragraph>
      A implantação destas medidas deverá ser acompanhada de treinamentos aos trabalhadores sobre sua eficiência e
      limitações. E ainda quando ocorrer a inviabilidade técnica, insuficiência na proteção, ou estiverem em
      estudo; deverão ser adotadas medidas, como seguem:
    </Paragraph>
    <Bullets
      items={[
        '1. Medidas de caráter administrativo ou de organização do trabalho;',
        '2. Utilização de Equipamento de Proteção Individual - EPI.'
      ]}
    />
    <Paragraph>
      A implantação de medidas de controle se fará pelo desenvolvimento de plano de ação específico, contemplando o
      estudo, desenvolvimento, implantação e avaliação de sua eficácia.
    </Paragraph>
    <Paragraph>
      As comunicações com os funcionários serão realizadas por meio do Portal Corporativo, e-mails institucionais e
      checklists de consulta direta aos trabalhadores. Esses canais têm como objetivo garantir a ampla divulgação e
      o engajamento dos funcionários em relação aos temas de segurança e saúde ocupacional promovidos pela
      Organização Bradesco, assegurando a ciência e o alinhamento de todos com as diretrizes estabelecidas.
    </Paragraph>
    <Paragraph>
      Os registros das ações implementadas serão efetuados conforme critérios de regularização e comprovação por
      evidências, mantendo as informações atualizadas como prevê o plano de ação já definido.
    </Paragraph>
    <Paragraph>
      O monitoramento das ações será realizado de forma contínua e sistemática, contemplando etapas de avaliação,
      ajustes, melhorias, comunicação e registro ao longo de toda a gestão. Esse acompanhamento será mantido até o
      prazo de renovação do Programa de Gerenciamento de Riscos (PGR), garantindo, assim, a eficácia das medidas
      implementadas e a promoção de um ambiente de trabalho seguro, confortável e saudável.
    </Paragraph>

    <Separator weight={15} />
  </View>
)

export default ClassificacaoRiscosFisicoQuimicoBiologicoSection
